import api from "services/api";

import { CampaignMonitorResponse, MonitorableType, MonitorSource, PortfolioResponse, ProgressReport, ResendResult } from "./monitor.types";

/**
 * invariant: nome da ficha do núcleo; só o reenvio da F0 (no caminho) e o `queue/close` (no corpo) o
 * recebem. As rotas do monitor levam o valor do enum.
 */
export const CORE_TYPE: Record<MonitorableType, string> = { contributario: "CONTRIBUTARIO" };

/** why: teto de chaves por chamada do `queue/close` (validação do controller no backend, T3). */
export const MAX_CLOSE_KEYS = 200;

/**
 * why: teto do motivo no `queue/close` (`REASON_MAX` do controller, T3); o reenvio corta em 500 sem
 * avisar (`build-manual-send-sql.ts`), então aqui se recusa em vez de gravar o motivo pela metade.
 */
export const MAX_REASON_LENGTH = 500;

const requiredReason = (reason: string, message: string): string => {
  const trimmed = reason.trim();
  if (!trimmed) throw new Error(message);
  if (trimmed.length > MAX_REASON_LENGTH) throw new Error(`O motivo aceita até ${MAX_REASON_LENGTH} caracteres.`);
  return trimmed;
};

/** invariant: toda visão do monitor (contrato, CNPJ, cartão de Conferir) começa por este prefixo; a ação invalida a família. */
export const MONITOR_QUERY_PREFIX = ["campaign-monitor"] as const;

export const monitorQueryKey = (source: MonitorSource, type: MonitorableType) =>
  [...MONITOR_QUERY_PREFIX, source.kind, source.kind === "WORKSPACE" ? source.workspaceId : source.organizationId, type] as const;

/** invariant: o monitor e o relatório de avanço leem o mesmo escopo (contrato ou CNPJ). */
const scopePath = (source: MonitorSource, type: MonitorableType): string =>
  source.kind === "WORKSPACE"
    ? `/v2/workspaces/${source.workspaceId}/campaigns/${type}`
    : `/v2/organizations/${source.organizationId}/campaigns/${type}`;

export async function fetchMonitor(source: MonitorSource, type: MonitorableType): Promise<CampaignMonitorResponse> {
  const { data } = await api.get(`${scopePath(source, type)}/monitor`);
  return data.data as CampaignMonitorResponse;
}

/** why: "Gerar relatório de avanço" (F4, Tarefa 17); leitura ao vivo do Snowflake, sob demanda. */
export async function generateProgressReport(source: MonitorSource, type: MonitorableType): Promise<ProgressReport> {
  const { data } = await api.post(`${scopePath(source, type)}/progress-report`);
  return data.data as ProgressReport;
}

export async function fetchPortfolio(sellerOrganizationId: string, type: MonitorableType): Promise<PortfolioResponse> {
  const { data } = await api.get(`/v2/sellers/${sellerOrganizationId}/campaigns/${type}/portfolio`);
  return data.data as PortfolioResponse;
}

/**
 * why: rota da F0; com `batchReason` vai como lote (`batch: true`), que o servidor só aceita com motivo.
 * hazard: a chave do adendo tem ":" e ","; sem codificar, o caminho quebra.
 * invariant: resolver não é enviar; quem chama lê `status` (FALHOU vem com HTTP 200).
 */
export async function resendNow(workspaceId: string, type: MonitorableType, occurrenceKey: string, batchReason?: string): Promise<ResendResult> {
  const body = batchReason === undefined ? {} : { reason: requiredReason(batchReason, "Reenviar em lote exige motivo."), batch: true };
  const { data } = await api.post(`/v2/workspaces/${workspaceId}/campaigns/${CORE_TYPE[type]}/occurrences/${encodeURIComponent(occurrenceKey)}/resend`, body);
  return data.data as ResendResult;
}

/** invariant: `closed` é o que o servidor fechou (chave já encerrada ou de contrato alheio conta zero); a tela afirma só isso. */
export async function closeForPeople(organizationId: string, type: MonitorableType, occurrenceKeys: string[], reason: string): Promise<{ closed: number }> {
  const trimmed = requiredReason(reason, "Encerrar exige motivo.");
  if (occurrenceKeys.length === 0 || occurrenceKeys.length > MAX_CLOSE_KEYS) {
    throw new Error(`Encerrar aceita de 1 até ${MAX_CLOSE_KEYS} pendências por vez.`);
  }
  const { data } = await api.post(`/v2/campaigns/${organizationId}/queue/close`, { type: CORE_TYPE[type], occurrenceKeys, reason: trimmed });
  return data.data as { closed: number };
}

/**
 * why: "Decidir pelo RH" é o "Definir optante" do app de Exceções. A spec §5.6 manda motivo
 * obrigatório; `requireReason` faz o servidor exigir o motivo por item (R17), sem mudar a tela de Exceções.
 * hazard: lista vazia grava SUCCESS no log do app sem nenhuma decisão; por isso é barrada.
 */
export async function decideByHr(exceptionWorkspaceActivityId: string, decisions: { taxPayerId: string; isOptant: boolean }[], reason: string): Promise<void> {
  const trimmed = requiredReason(reason, "Decidir pelo RH exige motivo.");
  if (decisions.length === 0) throw new Error("Decidir pelo RH exige ao menos uma pessoa.");
  await api.post(`/v2/apps/exception/${exceptionWorkspaceActivityId}`, {
    requireReason: true,
    payload: decisions.map((d) => ({ taxPayerId: d.taxPayerId, isNotOptant: !d.isOptant, reason: trimmed })),
  });
}
